// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useState } from 'react'
import { useAuth } from '../../auth/AuthContext'
import { LayoutDashboard, DollarSign, ScrollText, FileBarChart, HardDrive, Activity } from 'lucide-react'
import DashboardHome from './DashboardHome'
import OpsDashboardTab from './OpsDashboardTab'
import LogsErrorsTab from './LogsErrorsTab'
import ReportingTab from './ReportingTab'
import StorageTab from './StorageTab'
import LiveSessionsTab from './LiveSessionsTab'

type Sub = 'overview' | 'live' | 'cost' | 'storage' | 'logs' | 'reporting'

/**
 * System Monitor — the consolidated operational home. Sub-tabs for Overview (Control Room),
 * Cost analysis, Logs & Errors, and Reporting. Replaces the previously separate Dashboard +
 * Operations top-level tabs.
 */
export default function SystemMonitor() {
  const [sub, setSub] = useState<Sub>('overview')
  // D-SUPPORT-4 (Patrick): running cost is manager-tier only. Support agents/supervisors
  // get the operational tabs; Cost requires support.settings.manage (or the admin role).
  const { role, hasPermission } = useAuth()
  const canSeeCost = role === 'admin' || hasPermission('support.settings.manage')
  const allTabs: { id: Sub; label: string; icon: any }[] = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'live', label: 'Live Sessions', icon: Activity },
    { id: 'cost', label: 'Cost', icon: DollarSign },
    { id: 'storage', label: 'Storage', icon: HardDrive },
    { id: 'logs', label: 'Logs & Errors', icon: ScrollText },
    { id: 'reporting', label: 'Reporting', icon: FileBarChart },
  ]
  const tabs = allTabs.filter(t => (t.id === 'cost' ? canSeeCost : true))
  return (
    <div>
      <div style={subnav} role="tablist" aria-label="System monitor sections">
        {tabs.map(t => {
          const Icon = t.icon
          const active = sub === t.id
          return (
            <button key={t.id} role="tab" aria-selected={active} onClick={() => setSub(t.id)}
                    style={{ ...subTab, ...(active ? subTabActive : null) }}>
              <Icon size={15} aria-hidden="true" />
              <span>{t.label}</span>
            </button>
          )
        })}
      </div>
      <div role="tabpanel">
        {sub === 'overview' && <DashboardHome />}
        {sub === 'live' && <LiveSessionsTab />}
        {sub === 'cost' && <OpsDashboardTab />}
        {sub === 'storage' && <StorageTab />}
        {sub === 'logs' && <LogsErrorsTab />}
        {sub === 'reporting' && <ReportingTab />}
      </div>
    </div>
  )
}

const subnav: React.CSSProperties = {
  display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #e2e8f0', paddingBottom: 0,
}
const subTab: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', fontSize: 14, fontWeight: 600,
  color: '#64748b', background: 'none', border: 'none', borderBottom: '2px solid transparent',
  cursor: 'pointer', marginBottom: -1,
}
const subTabActive: React.CSSProperties = {
  color: '#0e7490', borderBottom: '2px solid #0891b2',
}
